import { ColumnMapping } from './types';

export const LOGICAL_FIELDS: Array<{
	key: keyof ColumnMapping;
	label: string;
	required: boolean;
	description?: string;
}> = [
	{ key: 'event_name', label: 'Event name', required: false, description: 'Required when scope = per-row' },
	{ key: 'event_date', label: 'Event date', required: false, description: 'Required when scope = per-row' },
	{ key: 'event_time', label: 'Event time', required: false },
	{ key: 'ticket_type_name', label: 'Ticket type name', required: true },
	{
		key: 'customer_email',
		label: 'Customer email',
		required: true,
		description: 'Used to group tickets into transactions',
	},
	{ key: 'customer_name', label: 'Customer name', required: false },
	{ key: 'seat_section', label: 'Seat section', required: false },
	{ key: 'seat_row', label: 'Seat row', required: false },
	{
		key: 'seat_number',
		label: 'Seat number / label',
		required: false,
		description: 'Required for events with assigned seating',
	},
	{ key: 'price', label: 'Price', required: false },
	{
		key: 'external_ticket_id',
		label: 'External ticket ID (barcode)',
		required: false,
		description: 'Enables re-import without duplicates',
	},
	{ key: 'order_id', label: 'External order ID', required: false },
];

export const TIMEZONE_OPTIONS = [
	'UTC',
	'America/New_York',
	'America/Chicago',
	'America/Denver',
	'America/Los_Angeles',
	'America/Phoenix',
	'America/Anchorage',
	'America/Honolulu',
	'America/Argentina/Buenos_Aires',
	'Europe/London',
];

export const DATE_FORMAT_PRESETS: Array<{ label: string; value: string }> = [
	{ label: 'M/D/YY (5/27/26)', value: '1/2/06' },
	{ label: 'M/D/YYYY (5/27/2026)', value: '1/2/2006' },
	{ label: 'YYYY-MM-DD (2026-05-27)', value: '2006-01-02' },
	{ label: 'DD/MM/YYYY (27/05/2026)', value: '02/01/2006' },
];

export const TIME_FORMAT_PRESETS: Array<{ label: string; value: string }> = [
	{ label: '12-hour with AM/PM (7:30 PM)', value: '3:04 PM' },
	{ label: '24-hour (19:30)', value: '15:04' },
	{ label: '24-hour with seconds (19:30:00)', value: '15:04:05' },
];

export const STEPS: Array<{ hash: string; label: string }> = [
	{ hash: '#1', label: 'Upload' },
	{ hash: '#2', label: 'Scope' },
	{ hash: '#3', label: 'Columns' },
	{ hash: '#4', label: 'Events' },
	{ hash: '#5', label: 'Options' },
	{ hash: '#6', label: 'Ticket types' },
	{ hash: '#7', label: 'Preview' },
	{ hash: '#8', label: 'Commit' },
];

export const goToStep = (history: { push: (path: string) => void }, currentPath: string, hash: string) => {
	history.push(currentPath + hash);
};

export const previewRowToString = (errors?: string[]): string => {
	if (!errors || errors.length === 0) return '';
	return errors.join(' • ');
};

// The backend returns this exact message when the import row exists in the
// DB but its CSV file has gone missing from storage (typically dev when
// /tmp gets wiped, or pod scheduling drift if GCS isn't configured yet).
// When we detect it, the wizard kicks the user back to step 1 so they can
// either discard the broken import or upload a fresh CSV.
export const STORED_FILE_MISSING = 'import has no stored file';

export const isFileMissingError = (err: any): boolean => {
	const msg = err?.response?.data?.error;
	return typeof msg === 'string' && msg === STORED_FILE_MISSING;
};

// React-side helper: pass an error from a step's onError callback. If it
// matches the missing-file case, surface a clear notification and kick the
// user back to step 1 so they can discard the import or upload a new CSV.
// Returns true when the error was handled (i.e. caller should NOT show its
// own generic toast).
//
// The `notify`/`notifyType` types are intentionally loose because the
// useToastNotification hook returns a value (Toast Id) we don't care about
// and accepts a wider props shape than what we use here.
export const handleFileMissingError = (
	err: any,
	deps: {
		notify: (opts: any) => any;
		notifyType: { ERROR: any };
		reset: () => void;
		navigate: (path: string) => void;
		basePath: string;
	}
): boolean => {
	if (!isFileMissingError(err)) return false;
	deps.notify({
		type: deps.notifyType.ERROR,
		message: 'This import lost its uploaded CSV (likely a server restart). Discard it or upload again.',
	});
	deps.reset();
	deps.navigate(`${deps.basePath}#1`);
	return true;
};
